import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../bizsuite-app';
import Modal from '../components/Modal';
import FormField from '../components/FormField';
import RegionPicker from '../components/RegionPicker';

export default function Offices() {
  const [offices, setOffices] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [search, setSearch] = useState('');

  // Calendars master — used by reminder/payroll modules to drive
  // weekly-off / preponement at the office level.
  useEffect(() => {
    apiFetch('/api/metadata/lookups?category=calendar').then(async (r) => {
      if (!r.ok) return;
      const d = await r.json();
      setCalendars((d.rows || d.lookups || []).filter((x) => x.status !== 'Inactive'));
    }).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/employees/offices');
      if (res.ok) {
        const data = await res.json();
        setOffices(data.offices || []);
      }
    } catch (err) { /* handled */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  function openEdit(office) {
    setEditRow(office ? {
      id: office.id,
      name: office.name || '',
      address: office.address || '',
      city: office.city || '',
      country: office.country || '',
      region_id: office.region_id || '',
      calendar_id: office.calendar_id || '',
      status: office.status || 'Active',
    } : {
      name: '', address: '', city: '', country: '', region_id: '', calendar_id: '', status: 'Active',
    });
    setEditModal(true);
  }

  async function saveOffice() {
    if (!editRow.name.trim()) return;
    try {
      await apiFetch('/api/employees/offices', {
        method: 'POST',
        body: JSON.stringify(editRow),
      });
      setEditModal(false);
      fetchData();
    } catch (err) { /* handled */ }
  }

  async function deleteOffice(id) {
    if (!confirm('Delete this office?')) return;
    try {
      await apiFetch(`/api/employees/offices/${id}`, { method: 'DELETE' });
      fetchData();
    } catch (err) { /* handled */ }
  }

  const filteredOffices = offices.filter(o => {
    if (!search) return true;
    const s = search.toLowerCase();
    return o.name.toLowerCase().includes(s) ||
           (o.city || '').toLowerCase().includes(s) ||
           (o.country || '').toLowerCase().includes(s) ||
           (o.address || '').toLowerCase().includes(s);
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 16 }}>
        <button className="btn btn-primary" onClick={() => openEdit(null)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Office
        </button>
      </div>

      <div style={{ marginBottom: 12 }}>
        <input type="text" placeholder="Search offices..." value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)',
            background: 'rgba(255,255,255,0.05)', color: '#e0e0e0', width: 300 }} />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>Loading...</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Address</th>
              <th>City</th>
              <th>Country</th>
              <th>Status</th>
              <th style={{ width: 100 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOffices.length === 0 ? (
              <tr><td colSpan="6" style={{ textAlign: 'center', color: '#888', padding: 24 }}>
                {search ? 'No matching offices found' : 'No offices defined yet. Click "New Office" to create one.'}
              </td></tr>
            ) : filteredOffices.map(o => (
              <tr key={o.id}>
                <td style={{ fontWeight: 500 }}>{o.name}</td>
                <td style={{ color: '#888' }}>{o.address || '—'}</td>
                <td>{o.city || '—'}</td>
                <td>{o.country || '—'}</td>
                <td>
                  <span className={`badge ${o.status === 'Active' ? 'badge-green' : 'badge-red'}`}>
                    {o.status}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn-neon btn-sm" onClick={() => openEdit(o)} title="Edit">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                    <button className="btn-neon btn-sm" onClick={() => deleteOffice(o.id)} title="Delete" style={{ '--page-neon': '#ff4444' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Edit / Create Modal */}
      <Modal title={editRow?.id ? 'Edit Office' : 'New Office'} isOpen={editModal} onClose={() => setEditModal(false)} size="md">
        {editRow && <>
          <FormField label="Office Name" name="name" required
            value={editRow.name} placeholder="e.g. Head Office, Branch - Mumbai"
            onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))} />
          <FormField label="Address" name="address" type="textarea" rows={2}
            value={editRow.address} placeholder="Full address"
            onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))} />
          <FormField label="City" name="city"
            value={editRow.city} placeholder="City"
            onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))} />
          {/* Country + Region — cascading from metadata_lookups masters.
              The free-text country field has been replaced; country name
              is derivable from the picked region's parent country. */}
          <RegionPicker label="Country & Region" value={editRow.region_id}
            onChange={(v) => setEditRow(prev => ({ ...prev, region_id: v }))}
            placeholder="Select region..." />
          {/* Calendar — drives reminder preponement at this location. The
              fallback chain is reminder → office → company → Mon–Fri. */}
          <FormField label="Calendar" name="calendar_id" type="select"
            value={editRow.calendar_id || ''}
            onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))}
            options={[
              { value: '', label: 'Inherit from company default' },
              ...calendars.map((c) => ({ value: c.id, label: c.name })),
            ]} />
          <FormField label="Status" name="status" type="select"
            value={editRow.status}
            onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))}
            options={[{ value: 'Active', label: 'Active' }, { value: 'Inactive', label: 'Inactive' }]} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
            <button className="btn btn-secondary" onClick={() => setEditModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveOffice}>Save</button>
          </div>
        </>}
      </Modal>
    </div>
  );
}
